import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  FaExclamationTriangle,
  FaFileExport,
  FaPowerOff,
  FaShieldAlt,
  FaTrashAlt,
  FaUndo,
} from 'react-icons/fa';
import {
  useNavigate,
} from 'react-router-dom';
import {
  useTranslation,
} from 'react-i18next';

import Header from '../components/Header';
import { supabase } from '../supabaseClient';

import {
  deactivateMyAccount,
  loadAccountLifecycleState,
  reactivateMyAccount,
  requestMyAccountDeletion,
  requestMyDataExport,
  signOutAfterLifecycleChange,
  type AccountLifecycleState,
} from '../lib/accountLifecycle';

type BusyAction =
  | 'deactivate'
  | 'reactivate'
  | 'export'
  | 'delete'
  | null;

export default function AccountLifecycle() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const english =
    i18n.language
      .toLowerCase()
      .startsWith('en');

  const [state, setState] =
    useState<AccountLifecycleState | null>(null);

  const [email, setEmail] =
    useState('');

  const [loading, setLoading] =
    useState(true);

  const [busy, setBusy] =
    useState<BusyAction>(null);

  const [error, setError] =
    useState<string | null>(null);

  const [notice, setNotice] =
    useState<string | null>(null);

  const text = useCallback((
    fr: string,
    en: string,
  ) => (
    english ? en : fr
  ), [english]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const {
        data,
        error: userError,
      } = await supabase.auth.getUser();

      if (userError) {
        throw userError;
      }

      if (!data.user) {
        navigate(
          '/login?redirect=/gestion-compte',
          { replace: true },
        );
        return;
      }

      setEmail(
        data.user.email ?? '',
      );

      const lifecycle =
        await loadAccountLifecycleState();

      if (!lifecycle) {
        throw new Error(
          text(
            'Le profil de compte est introuvable.',
            'The account profile could not be found.',
          ),
        );
      }

      setState(lifecycle);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : text(
              'Impossible de charger le compte.',
              'Unable to load the account.',
            ),
      );
    } finally {
      setLoading(false);
    }
  }, [
    navigate,
    text,
  ]);

  useEffect(() => {
    void load();
  }, [load]);

  const statusLabel = useMemo(() => {
    switch (state?.account_status) {
      case 'active':
        return text(
          'Actif',
          'Active',
        );

      case 'deactivated':
        return text(
          'Désactivé',
          'Deactivated',
        );

      case 'deletion_requested':
        return text(
          'Suppression demandée',
          'Deletion requested',
        );

      default:
        return '—';
    }
  }, [
    state?.account_status,
    text,
  ]);

  const perform = async (
    action: BusyAction,
    operation: () => Promise<void>,
  ) => {
    setBusy(action);
    setError(null);
    setNotice(null);

    try {
      await operation();
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : text(
              'Une erreur est survenue.',
              'An error occurred.',
            ),
      );
    } finally {
      setBusy(null);
    }
  };

  const handleDeactivate = () => {
    const confirmed = window.confirm(
      text(
        'Désactiver votre compte? Vous serez déconnecté. Vos commandes, réservations et données historiques ne seront pas supprimées.',
        'Deactivate your account? You will be signed out. Your orders, reservations and historical data will not be deleted.',
      ),
    );

    if (!confirmed) {
      return;
    }

    void perform(
      'deactivate',
      async () => {
        await deactivateMyAccount();
        await signOutAfterLifecycleChange();

        navigate(
          '/login?account=deactivated',
          { replace: true },
        );
      },
    );
  };

  const handleReactivate = () => {
    void perform(
      'reactivate',
      async () => {
        await reactivateMyAccount();

        setNotice(
          text(
            'Votre compte est réactivé.',
            'Your account has been reactivated.',
          ),
        );

        await load();
      },
    );
  };

  const handleExport = () => {
    void perform(
      'export',
      async () => {
        const requestId =
          await requestMyDataExport();

        setNotice(
          text(
            `Votre demande d’export a été enregistrée (${requestId.slice(0, 8)}…).`,
            `Your export request has been recorded (${requestId.slice(0, 8)}…).`,
          ),
        );

        await load();
      },
    );
  };

  const handleDeletion = () => {
    const confirmed = window.confirm(
      text(
        'Demander la suppression de votre compte? Votre accès sera désactivé immédiatement, mais aucune commande, réservation, entreprise ou donnée financière ne sera effacée automatiquement.',
        'Request account deletion? Your access will be disabled immediately, but no order, reservation, business or financial record will be automatically erased.',
      ),
    );

    if (!confirmed) {
      return;
    }

    void perform(
      'delete',
      async () => {
        await requestMyAccountDeletion();
        await signOutAfterLifecycleChange();

        navigate(
          '/login?account=deletion-requested',
          { replace: true },
        );
      },
    );
  };

  const inactive =
    state?.account_status === 'deactivated' ||
    state?.account_status === 'deletion_requested';

  return (
    <div className="min-h-screen bg-slate-50" data-account-shell="v216">
      <Header />

      <main className="mx-auto max-w-5xl px-4 pb-24 pt-28 sm:px-6 lg:px-8">
        <section className="overflow-hidden rounded-[2rem] bg-slate-950 p-8 text-white shadow-2xl md:p-10">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-widest text-sky-200">
                <FaShieldAlt />
                {text(
                  'Identité & confidentialité',
                  'Identity & privacy',
                )}
              </div>

              <h1 className="text-3xl font-black md:text-5xl">
                {text(
                  'Gestion du compte',
                  'Account management',
                )}
              </h1>

              <p className="mt-3 max-w-2xl text-slate-300">
                {text(
                  'Désactivez, réactivez ou gérez vos demandes de confidentialité sans supprimer aveuglément vos données transactionnelles.',
                  'Deactivate, reactivate or manage privacy requests without blindly deleting transactional records.',
                )}
              </p>

              {email && (
                <p className="mt-4 text-xs text-slate-400">
                  {email}
                </p>
              )}
            </div>

            <div className="rounded-2xl bg-white/10 px-5 py-4 ring-1 ring-white/10">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                {text(
                  'État du compte',
                  'Account status',
                )}
              </div>

              <div className="mt-1 text-xl font-black">
                {statusLabel}
              </div>
            </div>
          </div>
        </section>

        {error && (
          <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-5 text-sm font-semibold text-red-800">
            {error}
          </div>
        )}

        {notice && (
          <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-sm font-semibold text-emerald-800">
            {notice}
          </div>
        )}

        {loading ? (
          <div className="mt-8 rounded-3xl border border-slate-100 bg-white p-12 text-center shadow-sm">
            <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-slate-900" />
          </div>
        ) : state ? (
          <div className="mt-8 grid gap-6 md:grid-cols-2">
            {inactive ? (
              <section className="rounded-3xl border border-emerald-100 bg-white p-7 shadow-sm">
                <FaUndo className="text-2xl text-emerald-600" />

                <h2 className="mt-4 text-xl font-black text-slate-900">
                  {text(
                    'Réactiver mon compte',
                    'Reactivate my account',
                  )}
                </h2>

                <p className="mt-2 text-sm leading-6 text-slate-600">
                  {text(
                    'La réactivation restaure vos permissions précédentes. Si une suppression était demandée mais non traitée, elle sera annulée.',
                    'Reactivation restores your previous permissions. If a deletion request was pending but not processed, it will be cancelled.',
                  )}
                </p>

                <button
                  type="button"
                  onClick={handleReactivate}
                  disabled={busy !== null}
                  className="mt-6 w-full rounded-2xl bg-emerald-600 px-5 py-3 font-black text-white disabled:opacity-50"
                >
                  {busy === 'reactivate'
                    ? text(
                        'Réactivation…',
                        'Reactivating…',
                      )
                    : text(
                        'Réactiver',
                        'Reactivate',
                      )}
                </button>
              </section>
            ) : (
              <section className="rounded-3xl border border-amber-100 bg-white p-7 shadow-sm">
                <FaPowerOff className="text-2xl text-amber-600" />

                <h2 className="mt-4 text-xl font-black text-slate-900">
                  {text(
                    'Désactiver temporairement',
                    'Temporarily deactivate',
                  )}
                </h2>

                <p className="mt-2 text-sm leading-6 text-slate-600">
                  {text(
                    'Votre accès aux espaces privés sera bloqué. Votre entreprise, vos commandes, vos réservations et votre historique ne seront pas supprimés.',
                    'Access to private spaces will be blocked. Your business, orders, reservations and history will not be deleted.',
                  )}
                </p>

                {!state.can_self_deactivate && (
                  <div className="mt-4 flex gap-3 rounded-2xl bg-amber-50 p-4 text-sm text-amber-800">
                    <FaExclamationTriangle className="mt-1 shrink-0" />
                    {text(
                      'Un administrateur plateforme ne peut pas se désactiver lui-même afin d’éviter un verrouillage administratif.',
                      'A platform administrator cannot self-deactivate to prevent administrative lockout.',
                    )}
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleDeactivate}
                  disabled={
                    busy !== null ||
                    !state.can_self_deactivate
                  }
                  className="mt-6 w-full rounded-2xl bg-amber-500 px-5 py-3 font-black text-white disabled:opacity-50"
                >
                  {text(
                    'Désactiver mon compte',
                    'Deactivate my account',
                  )}
                </button>
              </section>
            )}

            <section className="rounded-3xl border border-blue-100 bg-white p-7 shadow-sm">
              <FaFileExport className="text-2xl text-blue-600" />

              <h2 className="mt-4 text-xl font-black text-slate-900">
                {text(
                  'Exporter mes données',
                  'Export my data',
                )}
              </h2>

              <p className="mt-2 text-sm leading-6 text-slate-600">
                {text(
                  'Crée une demande d’export auditable. La génération complète du dossier de données sera traitée séparément.',
                  'Creates an auditable export request. Full data-package generation is processed separately.',
                )}
              </p>

              {state.pending_export_request && (
                <div className="mt-4 rounded-2xl bg-blue-50 p-4 text-sm font-semibold text-blue-800">
                  {text(
                    'Une demande d’export est déjà en attente.',
                    'An export request is already pending.',
                  )}
                </div>
              )}

              <button
                type="button"
                onClick={handleExport}
                disabled={
                  busy !== null ||
                  state.pending_export_request
                }
                className="mt-6 w-full rounded-2xl bg-blue-600 px-5 py-3 font-black text-white disabled:opacity-50"
              >
                {text(
                  'Demander mon export',
                  'Request my export',
                )}
              </button>
            </section>

            <section className="md:col-span-2 rounded-3xl border border-red-100 bg-white p-7 shadow-sm">
              <FaTrashAlt className="text-2xl text-red-600" />

              <h2 className="mt-4 text-xl font-black text-slate-900">
                {text(
                  'Demander la suppression',
                  'Request deletion',
                )}
              </h2>

              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                {text(
                  'Cette action enregistre une demande et désactive votre accès. Elle ne supprime pas immédiatement les données liées aux commandes, paiements, réservations, entreprises ou obligations de conservation.',
                  'This records a deletion request and disables access. It does not immediately erase records linked to orders, payments, reservations, businesses or retention obligations.',
                )}
              </p>

              {!state.can_self_deactivate && (
                <div className="mt-4 flex gap-3 rounded-2xl bg-red-50 p-4 text-sm text-red-800">
                  <FaExclamationTriangle className="mt-1 shrink-0" />
                  {text(
                    'La suppression du compte administrateur principal doit être traitée par une procédure administrative distincte.',
                    'Deletion of the primary platform administrator account must use a separate administrative procedure.',
                  )}
                </div>
              )}

              <button
                type="button"
                onClick={handleDeletion}
                disabled={
                  busy !== null ||
                  !state.can_self_deactivate ||
                  state.pending_deletion_request
                }
                className="mt-6 rounded-2xl bg-red-600 px-6 py-3 font-black text-white disabled:opacity-50"
              >
                {state.pending_deletion_request
                  ? text(
                      'Suppression déjà demandée',
                      'Deletion already requested',
                    )
                  : text(
                      'Demander la suppression',
                      'Request deletion',
                    )}
              </button>
            </section>
          </div>
        ) : null}
      </main>
    </div>
  );
}
